import React, { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import { CheckCircle2, AlertCircle, Circle, Star } from "lucide-react";
import type { Task, ProgressItem } from "../types";
import { useMobile } from "../hooks/useMobile";

interface TaskCardProps {
  task: Task & {
    difficulty: "easy" | "medium" | "hard";
    domain: string;
  };
  progress?: ProgressItem;
  domainColor: string;
  onDomainClick?: (domain: string) => void;
  isDomainSelected?: boolean;
  onTagClick?: (tag: string) => void;
  selectedTags?: string[];
}

export const TaskCard = ({ task, progress, domainColor, onDomainClick, isDomainSelected, onTagClick, selectedTags = [] }: TaskCardProps) => {
  const [showErrorPopup, setShowErrorPopup] = useState(false);
  const [popupPosition, setPopupPosition] = useState<"bottom" | "top">("bottom");
  const [difficultyTooltipPos, setDifficultyTooltipPos] = useState<{ top: number; right: number } | null>(null);
  const popupRef = useRef<HTMLDivElement>(null);
  const errorBlockRef = useRef<HTMLDivElement>(null);
  const difficultyRef = useRef<HTMLDivElement>(null);
  const isMobile = useMobile();

  // Ring при выделении домена (приглушённая палитра — нейтральный ring)
  const getDomainRingClass = (domainColor: string): string => {
    const ringMap: Record<string, string> = {
      "border-blue-200": "ring-2 ring-blue-500 ring-offset-2",
      "border-purple-200": "ring-2 ring-purple-500 ring-offset-2",
      "border-indigo-200": "ring-2 ring-indigo-500 ring-offset-2",
      "border-pink-200": "ring-2 ring-pink-500 ring-offset-2",
      "border-orange-200": "ring-2 ring-orange-500 ring-offset-2",
      "border-amber-200": "ring-2 ring-amber-500 ring-offset-2",
      "border-cyan-200": "ring-2 ring-cyan-500 ring-offset-2",
      "border-violet-200": "ring-2 ring-violet-500 ring-offset-2",
      "border-neutral-200": "ring-2 ring-neutral-400 ring-offset-2",
      "border-slate-200": "ring-2 ring-slate-400 ring-offset-2",
      "border-stone-200": "ring-2 ring-stone-400 ring-offset-2",
      "border-zinc-200": "ring-2 ring-zinc-400 ring-offset-2",
    };
    for (const [borderClass, ringClass] of Object.entries(ringMap)) {
      if (domainColor.includes(borderClass)) return ringClass;
    }
    return "ring-2 ring-neutral-400 ring-offset-2";
  };
  
  // Если прогресса нет, считаем idle
  const progressStatus = progress?.status ?? "idle";
  
  // Проверяем, помещается ли попап на экране относительно блока с ошибкой
  useEffect(() => {
    if (!showErrorPopup) {
      // Сбрасываем позицию при скрытии
      setPopupPosition("bottom");
      return;
    }

    if (errorBlockRef.current) {
      const errorBlock = errorBlockRef.current;
      const errorBlockRect = errorBlock.getBoundingClientRect();
      
      // Максимальная высота попапа из CSS (max-h-64 = 16rem = 256px) + отступы
      const estimatedPopupHeight = 256 + 32; // 256px max-height + padding
      const spaceBelow = window.innerHeight - errorBlockRect.bottom;
      const spaceAbove = errorBlockRect.top;
      
      // Если снизу не хватает места, но сверху есть больше места - показываем сверху
      if (spaceBelow < estimatedPopupHeight && spaceAbove > spaceBelow) {
        setPopupPosition("top");
      } else {
        setPopupPosition("bottom");
      }
      
      // Дополнительная проверка после рендера для точности
      requestAnimationFrame(() => {
        if (!popupRef.current || !errorBlockRef.current) return;
        
        const popup = popupRef.current;
        const actualPopupHeight = popup.offsetHeight;
        const errorBlockRect = errorBlockRef.current.getBoundingClientRect();
        const actualSpaceBelow = window.innerHeight - errorBlockRect.bottom;
        const actualSpaceAbove = errorBlockRect.top;
        
        // Если реальная высота больше, чем помещается снизу, но помещается сверху
        if (actualSpaceBelow < actualPopupHeight && actualSpaceAbove > actualSpaceBelow) {
          setPopupPosition("top");
        } else if (actualSpaceBelow >= actualPopupHeight) {
          setPopupPosition("bottom");
        }
      });
    }
  }, [showErrorPopup]);
  
  const handleDomainClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    onDomainClick?.(task.domain);
  };
  
  const handleTagClick = (e: React.MouseEvent, tag: string) => {
    e.preventDefault();
    e.stopPropagation();
    onTagClick?.(tag);
  };

  const statusConfig: Record<
    "idle" | "success" | "error",
    { icon: React.ComponentType<{ className?: string }>; bg: string; border: string; iconColor: string; label: string }
  > = {
    idle: {
      icon: Circle,
      bg: "bg-neutral-light",
      border: "border-neutral-border",
      iconColor: "text-neutral-text/40",
      label: "Новая задача",
    },
    success: {
      icon: CheckCircle2,
      bg: "bg-accent",
      border: "border-primary/30",
      iconColor: "text-accent-foreground",
      label: "Выполнено",
    },
    error: {
      icon: AlertCircle,
      bg: "bg-red-50",
      border: "border-red-200",
      iconColor: "text-red-600",
      label: "Требуется исправление",
    },
  };

  const status = statusConfig[progressStatus];
  const StatusIcon = status.icon;

  const defaultDifficultyComments: Record<string, string> = {
    easy: "Простая задача для освоения базового SQL.",
    medium: "Потребуется уверенное владение SQL",
    hard: "Сложный кейс",
  };

  const difficultyMap: Record<
    TaskCardProps["task"]["difficulty"],
    { value: number; title: string; label: string }
  > = {
    easy: { value: 1, title: "Easy", label: "Лёгкая" },
    medium: { value: 2, title: "Medium", label: "Средняя" },
    hard: { value: 3, title: "Hard", label: "Сложная" },
  };

  const difficulty = difficultyMap[task.difficulty];
  const difficultyComment = task.difficultyComment || defaultDifficultyComments[task.difficulty];

  // Обрезаем текст ошибки до 70 символов
  const truncatedErrorMsg = progress?.error_msg && progress.error_msg.length > 70
    ? progress.error_msg.substring(0, 70) + "..."
    : progress?.error_msg;

  return (
    <li className={`group relative ${showErrorPopup ? "z-[9999]" : ""}`}>
      <Link
        to={`/task/${task.id}`}
        className="block bg-white border-2 rounded-xl hover:shadow-md hover:scale-[1.02] transition-all border-neutral-border overflow-hidden"
      >
        {/* Верхняя часть карточки: контент */}
        <div className="p-5">
          <div className="flex items-start justify-between gap-4 min-w-0">
            {/* Левая часть: номер и статус */}
            <div className="flex items-start gap-4 flex-1 min-w-0">
              {/* Индикатор статуса с тултипом */}
              <div className="relative group/status">
                <div
                  data-onboarding-id="task-card-status"
                  className={`${isMobile ? "w-10 h-10" : "w-12 h-12"} rounded-xl flex items-center justify-center border-2 transition-colors flex-shrink-0 ${status.bg} ${status.border}`}
                  aria-describedby={`status-tooltip-${task.id}`}
                >
                  <StatusIcon className={`${isMobile ? "w-5 h-5" : "w-6 h-6"} ${status.iconColor}`} />
                </div>
                <div
                  id={`status-tooltip-${task.id}`}
                  className="absolute left-full top-1/2 -translate-y-1/2 ml-2 hidden group-hover/status:block bg-neutral-text text-white text-xs rounded-lg px-3 py-2 shadow-xl z-50 whitespace-nowrap pointer-events-none"
                  role="tooltip"
                >
                  {status.label}
                  <div className="absolute right-full top-1/2 -translate-y-1/2 -mr-1 w-2 h-2 bg-neutral-text rotate-45"></div>
                </div>
              </div>

              {/* Контент задачи */}
              <div className="flex-1 min-w-0">
                {/* Заголовок */}
                <h3 className="text-lg font-semibold text-neutral-text transition-colors mb-1.5">
                  <span className="font-semibold">{task.id}. </span>
                  {task.title}
                </h3>

                {/* Описание (если есть) */}
                {task.description && (
                  <p className="text-sm text-neutral-text/60 line-clamp-2 mb-3">
                    {task.description}
                  </p>
                )}

                {/* Метаданные: домен и теги */}
                <div className="flex flex-wrap items-center gap-2">
                  <button
                    data-onboarding-id="task-card-domain"
                    onClick={handleDomainClick}
                    className={`px-3 py-1.5 text-sm font-semibold rounded-md shadow-sm transition-all cursor-pointer hover:scale-105 hover:shadow-md ${domainColor.replace(/border border-(\w+-\d+)/g, 'border-2 border-$1')} ${
                      isDomainSelected ? getDomainRingClass(domainColor) : ""
                    }`}
                  >
                    {task.domain}
                  </button>
                  {task.tag?.map(tag => {
                    const isTagSelected = selectedTags.includes(tag);
                    return (
                      <button
                        key={tag}
                        data-onboarding-id="task-card-tag"
                        onClick={(e) => handleTagClick(e, tag)}
                        className={`px-2 py-0.5 text-xs rounded border transition-all cursor-pointer ${
                          isTagSelected
                            ? "bg-primary text-white border-primary shadow-sm"
                            : "bg-neutral-light text-neutral-text/70 border-neutral-border hover:bg-primary/10 hover:border-primary/30 hover:scale-105"
                        }`}
                      >
                        {tag}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Правая часть: сложность */}
            <div className="flex flex-col items-end gap-3 flex-shrink-0">
              {/* Сложность с тултипом */}
              <div
                ref={difficultyRef}
                data-onboarding-id="task-card-difficulty"
                className="flex gap-1 items-center cursor-default"
                onMouseEnter={() => {
                  if (difficultyRef.current) {
                    const rect = difficultyRef.current.getBoundingClientRect();
                    setDifficultyTooltipPos({
                      top: rect.bottom + 6,
                      right: window.innerWidth - rect.right,
                    });
                  }
                }}
                onMouseLeave={() => setDifficultyTooltipPos(null)}
              >
                {[1, 2, 3].map(i => (
                  <Star
                    key={i}
                    className={`w-4 h-4 transition-colors ${
                      i <= difficulty.value
                        ? "fill-yellow-500 text-yellow-500"
                        : "fill-transparent text-neutral-border"
                    }`}
                  />
                ))}
              </div>

              {/* Тултип сложности через портал — не обрезается overflow-hidden карточки */}
              {difficultyTooltipPos && createPortal(
                <div
                  className="fixed bg-neutral-text text-white text-xs rounded-lg p-3 w-56 shadow-xl z-[9999] pointer-events-none"
                  style={{ top: difficultyTooltipPos.top, right: difficultyTooltipPos.right }}
                >
                  <div className="font-semibold mb-1">{difficulty.label}</div>
                  <div className="text-neutral-light/90 leading-relaxed">{difficultyComment}</div>
                </div>,
                document.body
              )}
            </div>
          </div>
        </div>

        {/* Блок ошибки — полноширинная полоса внизу карточки, ровные отступы */}
        {progressStatus === "error" && progress?.error_msg && (
          <div
            ref={errorBlockRef}
            className="relative border-t border-red-100 bg-red-50/80 px-5 py-3"
            onMouseEnter={() => progress.error_msg && progress.error_msg.length > 70 && setShowErrorPopup(true)}
            onMouseLeave={() => setShowErrorPopup(false)}
          >
            <div className="flex items-start gap-2">
              <AlertCircle className="w-4 h-4 flex-shrink-0 text-red-500 mt-0.5" aria-hidden />
              <p className="text-sm leading-relaxed break-words text-red-700 line-clamp-2 overflow-hidden">
                {truncatedErrorMsg}
              </p>
            </div>

            {/* Попап с полным текстом ошибки */}
            {showErrorPopup && progress.error_msg.length > 70 && (
              <div
                ref={popupRef}
                className={`absolute left-0 right-0 z-[9999] animate-slideIn ${
                  popupPosition === "top"
                    ? "bottom-full mb-2"
                    : "top-full mt-2"
                }`}
                onMouseEnter={() => setShowErrorPopup(true)}
                onMouseLeave={() => setShowErrorPopup(false)}
              >
                <div
                  className="rounded-lg border border-red-200 bg-white shadow-xl p-4 flex items-start gap-3"
                  role="alert"
                >
                  <AlertCircle size={20} className="text-red-500 flex-shrink-0 mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-sm mb-1 text-red-800">Полный текст ошибки</div>
                    <div className="text-sm leading-relaxed whitespace-pre-wrap break-words text-red-700 max-h-64 overflow-y-auto">
                      {progress.error_msg}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </Link>
    </li>
  );
};
